import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay, faPause, faStop, faTrash, faSpinner, faCheckCircle,
  faTimesCircle, faExclamationCircle, faChevronDown, faChevronRight,
  faClock, faCalendarCheck, faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import ScanUrlTable from './ScanUrlTable';

const SCAN_TYPE_LABEL = {
  all: 'All attacks', xss: 'XSS', stored_xss: 'Stored XSS', sqli: 'SQL Injection',
  nosqli: 'NoSQL Injection', rce: 'Command Injection', code_injection: 'Code Injection',
  lfi: 'Path Traversal', rfi: 'Remote File Include', ssti: 'Template Injection',
  xxe: 'XXE', xpath: 'XPath Injection', ldap: 'LDAP Injection',
  deserialization: 'Deserialization', ssrf: 'SSRF', csrf: 'CSRF', crlf: 'CRLF Injection',
  open_redirect: 'Open Redirect', host_header: 'Host Header', hpp: 'Param Pollution',
  padding_oracle: 'Padding Oracle', session_fixation: 'Session Fixation',
  http_smuggling: 'HTTP Smuggling',
  prototype_pollution: 'Proto Pollution',
  cache_poisoning: 'Cache Poisoning',
  jwt_attack: 'JWT Attack',
  h2c_smuggling: 'H2C Smuggling'
};

// Local timestamp for accountability (SOC "did you test at time X?").
const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('sv-SE').replace('T', ' '); } catch { return '—'; }
};

const fmtDuration = (scan) => {
  if (!scan.startedAt) return '—';
  const start = new Date(scan.startedAt);
  const end = scan.completedAt ? new Date(scan.completedAt) : new Date();
  const s = Math.max(0, Math.floor((end - start) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

// Distinct modules (phases) that ran for this scan, derived from per-URL phases.
const scanModules = (scan) => {
  const map = new Map();
  (scan.urlDetails || []).forEach(u => (u.phases || []).forEach(p => {
    if (p.key && !map.has(p.key)) map.set(p.key, { key: p.key, label: p.label || p.key, status: p.status });
  }));
  if (map.size) return [...map.values()];
  if (scan.scanType && scan.scanType !== 'all') return [{ key: scan.scanType, label: SCAN_TYPE_LABEL[scan.scanType] || scan.scanType }];
  return [];
};

const ScanQueue = ({ scans = [], selectedScan, onSelect, onPause, onResume, onCancel, onDelete, onViewResults }) => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [expanded, setExpanded] = useState(new Set());

  const scansList = Array.isArray(scans) ? scans : [];

  const toggle = (id, e) => {
    e?.stopPropagation();
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'running': return <FontAwesomeIcon icon={faSpinner} spin className="text-blue-400" />;
      case 'paused': return <FontAwesomeIcon icon={faPause} className="text-yellow-400" />;
      case 'completed': return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />;
      case 'failed': return <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />;
      case 'cancelled': return <FontAwesomeIcon icon={faExclamationCircle} className="text-gray-400" />;
      default: return <FontAwesomeIcon icon={faPlay} className="text-gray-400" />;
    }
  };

  const progressPct = (scan) => {
    if (!scan.progress || !scan.progress.totalTests) return 0;
    return Math.round((scan.progress.completedTests / scan.progress.totalTests) * 100);
  };

  // Theme helpers
  const cardBase = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const cardSel = darkMode ? 'bg-gray-800 border-blue-500 ring-1 ring-blue-500/50' : 'bg-blue-50 border-blue-400 ring-1 ring-blue-300';
  const textPri = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textSec = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textMut = darkMode ? 'text-gray-500' : 'text-gray-400';
  const statBox = darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200';
  const iconBtn = darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100';

  return (
    <div className="p-4">
      <h3 className={`text-base font-semibold mb-3 ${textPri}`}>{t('webTesting.activeScanQueue')}</h3>

      {scansList.length === 0 ? (
        <div className={`rounded-lg border p-8 text-center ${cardBase} ${textSec}`}>
          <p className="text-sm">{t('webTesting.noScansYet')}</p>
          <p className={`text-xs mt-1 ${textMut}`}>{t('webTesting.rightClickToStartScan')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {scansList.map((scan) => {
            const sel = selectedScan?._id === scan._id;
            const modules = scanModules(scan);
            const active = scan.status === 'running' || scan.status === 'paused';
            return (
              <div key={scan._id} className={`rounded-lg border p-3 transition-colors ${sel ? cardSel : cardBase}`}>
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0 cursor-pointer" onClick={() => onSelect(scan)}>
                    <span className="mt-0.5">{statusIcon(scan.status)}</span>
                    <div className="min-w-0">
                      <div className={`text-sm font-medium truncate ${textPri}`}>{scan.scanName}</div>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          {SCAN_TYPE_LABEL[scan.scanType] || scan.scanType}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-medium ${
                          scan.status === 'completed' ? (darkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700') :
                          scan.status === 'running' ? (darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700') :
                          scan.status === 'failed' ? (darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700') :
                          (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')
                        }`}>{scan.status}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {scan.status === 'running' && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); onPause(scan._id); }} className={`p-1.5 rounded ${iconBtn}`} title={t('common.pause')}><FontAwesomeIcon icon={faPause} /></button>
                        <button onClick={(e) => { e.stopPropagation(); onCancel(scan._id); }} className="p-1.5 rounded text-red-400 hover:bg-red-900/20" title={t('common.cancel')}><FontAwesomeIcon icon={faStop} /></button>
                      </>
                    )}
                    {scan.status === 'paused' && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); onResume(scan._id); }} className={`p-1.5 rounded ${iconBtn}`} title={t('common.resume')}><FontAwesomeIcon icon={faPlay} /></button>
                        <button onClick={(e) => { e.stopPropagation(); onCancel(scan._id); }} className="p-1.5 rounded text-red-400 hover:bg-red-900/20" title={t('common.cancel')}><FontAwesomeIcon icon={faStop} /></button>
                      </>
                    )}
                    {(scan.status === 'completed' || scan.status === 'cancelled' || scan.status === 'failed') && (
                      <button onClick={(e) => { e.stopPropagation(); onDelete && onDelete(scan._id); }} className="p-1.5 rounded text-red-400 hover:bg-red-900/20" title={t('common.delete')}><FontAwesomeIcon icon={faTrash} /></button>
                    )}
                  </div>
                </div>

                {/* Accountability: start / end / duration (persisted for completed scans) */}
                <div className={`flex items-center gap-x-4 gap-y-1 flex-wrap mt-2 text-[11px] ${textSec}`}>
                  <span className="flex items-center gap-1"><FontAwesomeIcon icon={faPlay} className="text-[9px]" /> Started: <span className={textPri}>{fmtDateTime(scan.startedAt)}</span></span>
                  <span className="flex items-center gap-1"><FontAwesomeIcon icon={faCalendarCheck} className="text-[9px]" /> Ended: <span className={textPri}>{scan.completedAt ? fmtDateTime(scan.completedAt) : (active ? 'in progress' : '—')}</span></span>
                  <span className="flex items-center gap-1"><FontAwesomeIcon icon={faClock} className="text-[9px]" /> {t('common.duration')}: <span className={textPri}>{fmtDuration(scan)}</span></span>
                </div>

                {/* Modules that ran (accountability) */}
                {modules.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-2">
                    <FontAwesomeIcon icon={faLayerGroup} className={`text-[10px] ${textMut}`} title="Modules run" />
                    {modules.map(m => (
                      <span key={m.key} className={`text-[10px] px-1.5 py-0.5 rounded border ${
                        m.status === 'done' ? (darkMode ? 'border-green-800 bg-green-900/30 text-green-400' : 'border-green-200 bg-green-50 text-green-700') :
                        m.status === 'running' ? (darkMode ? 'border-blue-800 bg-blue-900/30 text-blue-400' : 'border-blue-200 bg-blue-50 text-blue-700') :
                        m.status === 'error' ? (darkMode ? 'border-red-800 bg-red-900/30 text-red-400' : 'border-red-200 bg-red-50 text-red-700') :
                        (darkMode ? 'border-gray-700 bg-gray-900 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500')
                      }`}>{m.label}</span>
                    ))}
                  </div>
                )}

                {/* Progress (running/paused) */}
                {active && scan.progress && (
                  <div className="mt-2">
                    <div className={`w-full rounded-full h-1.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${progressPct(scan)}%` }} />
                    </div>
                    <div className={`flex items-center justify-between mt-1 text-[11px] ${textSec}`}>
                      <span>
                        {scan.progress.totalTests <= 1
                          ? `${scan.statistics?.requestsSent || 0} requests sent`
                          : `${scan.progress.completedTests} / ${scan.progress.totalTests} URLs (${progressPct(scan)}%)`
                        }
                      </span>
                      {scan.progress.currentTest && <span className="truncate ml-2 font-mono">{scan.progress.currentTest}</span>}
                    </div>
                    {scan.progress.currentUrl && (
                      <div className={`text-[11px] mt-0.5 truncate font-mono ${textMut}`} title={scan.progress.currentUrl}>{scan.progress.currentUrl}</div>
                    )}
                  </div>
                )}

                {/* URL detail expansion */}
                {((scan.enumeratedURLs && scan.enumeratedURLs.length > 0) || (scan.urlDetails && scan.urlDetails.length > 0)) && (
                  <div className="mt-2">
                    <button
                      onClick={(e) => toggle(scan._id, e)}
                      className={`text-[11px] px-2 py-1 rounded flex items-center gap-1.5 ${iconBtn}`}
                    >
                      <FontAwesomeIcon icon={expanded.has(scan._id) ? faChevronDown : faChevronRight} className="text-[9px]" />
                      {expanded.has(scan._id) ? t('webTesting.hideURLs') : t('webTesting.viewURLs')} ({scan.urlDetails?.length || scan.enumeratedURLs?.length || 0})
                    </button>
                    {expanded.has(scan._id) && <div className="mt-2"><ScanUrlTable scan={scan} /></div>}
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {[
                    { label: t('webTesting.vulnerabilities'), value: scan.progress?.vulnerabilitiesFound || 0, accent: (scan.progress?.vulnerabilitiesFound || 0) > 0 ? 'text-red-500' : textPri, clickable: (scan.progress?.vulnerabilitiesFound || 0) > 0 },
                    { label: t('webTesting.urlsTested'), value: scan.statistics?.urlsTested || 0, accent: textPri },
                    { label: t('webTesting.requests'), value: scan.statistics?.requestsSent || 0, accent: textPri },
                    { label: t('common.duration'), value: fmtDuration(scan), accent: textPri }
                  ].map((s, i) => (
                    <div
                      key={i}
                      className={`rounded border px-2 py-1.5 ${statBox} ${s.clickable ? 'cursor-pointer hover:border-red-500/50' : ''}`}
                      onClick={s.clickable ? (e) => { e.stopPropagation(); onViewResults?.(scan); } : undefined}
                      title={s.clickable ? 'View findings' : undefined}
                    >
                      <div className={`text-[10px] ${textMut}`}>{s.label}</div>
                      <div className={`text-lg font-semibold ${s.accent}`}>{s.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScanQueue;
